import { Card } from 'antd';
import type { ReactNode } from 'react';

/** Một ô số liệu. */
export interface StatTile {
  readonly key: string;
  readonly label: string;
  readonly icon: ReactNode;
  /** Màu của biểu tượng; nền ô biểu tượng là chính màu này, nhạt đi. Con số luôn là đen than. */
  readonly color: string;
  /** Rỗng khi chưa tải xong hoặc tải hỏng — ô hiện dấu gạch. */
  readonly value: number | undefined;
  /** Chữ hiển thị thay cho con số, ví dụ "1,2 tỷ". Bỏ trống thì hiện con số định dạng tiếng Việt. */
  readonly display?: string;
  /** Một dòng nhỏ dưới nhãn, ví dụ "3 quá hạn". */
  readonly hint?: ReactNode;
  /** Bấm vào ô — thường là mở danh sách đứng sau con số. */
  readonly onClick?: () => void;
}

/**
 * Dải ô số liệu — biểu tượng màu, con số lớn, nhãn chữ in hoa nhỏ.
 *
 * Dùng chung cho trang chủ và các trang quản lý nội dung website (dự án tiêu biểu, blog), để các
 * trang nhìn cùng một kiểu.
 *
 * Con số giữ màu đen than chứ không tô theo màu ô: bốn con số bốn màu thì mắt đọc màu trước khi đọc
 * số. Màu chỉ nằm ở biểu tượng để phân biệt các ô. Lưới tự chia đều theo bề rộng, nên bốn hay năm ô
 * đều thẳng hàng mà không để một ô lẻ xuống dòng.
 */
export function StatTiles({ items }: { readonly items: readonly StatTile[] }): ReactNode {
  return (
    <div className="erp-stat-grid">
      {items.map((item) => (
        <Card
          key={item.key}
          size="small"
          hoverable={item.onClick !== undefined}
          onClick={item.onClick}
          styles={{ body: { padding: '16px 18px' } }}
        >
          <div className="erp-stat">
            <div
              className="erp-stat-icon"
              style={{
                color: item.color,
                // Nền là chính màu biểu tượng, độ đậm khoảng 10%.
                background: `${item.color}1a`,
              }}
            >
              {item.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="erp-stat-value">
                {item.value === undefined ? '—' : (item.display ?? item.value.toLocaleString('vi-VN'))}
              </div>
              <div className="erp-stat-label">{item.label}</div>
              {item.hint === undefined ? null : <div className="erp-stat-hint">{item.hint}</div>}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
